/**
 * The reject dialog's quick-pick reasons — this console's own managed list (doc 05-admin/06 §5).
 *
 * Add, edit, reorder, retire; there is no delete, so a reason's history stays in the audit log.
 * The body is what the pandit reads, word for word, so the server runs it through the content
 * lint and holds it to the 500 characters `rejection_reason` stores.
 */
import { useState } from 'react';
import { StatusChip } from '../components/primitives';
import { Input } from '../components/forms';
import { ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useCreateRejectionTemplate,
  useRejectionTemplates,
  useUpdateRejectionTemplate,
  type RejectionTemplate,
} from '../lib/api/hooks/kyc';

/** The live ones, in the order the reject dialog offers them. */
export function liveQuickPicks(items: readonly RejectionTemplate[] | undefined): { label: string; body: string }[] {
  return (items ?? []).filter((t) => t.is_active).map((t) => ({ label: t.label, body: t.body }));
}

/** Why `raw` is not a position the server keeps (0–999), or null. Never quietly read as 0. */
export function positionError(raw: string): string | null {
  return /^\s*\d{1,3}\s*$/.test(raw) ? null : 'A whole number from 0 to 999';
}

export function KycRejectionTemplates() {
  const { scope, toast } = useConsole();
  const list = useRejectionTemplates();
  const items = list.data?.items ?? [];
  const create = useCreateRejectionTemplate();
  const update = useUpdateRejectionTemplate();
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [label, setLabel] = useState('');
  const [body, setBody] = useState('');
  const [order, setOrder] = useState('0');
  /* Both writes change what a pandit can be told, so both pass through the confirm. */
  const [confirming, setConfirming] = useState<'save' | RejectionTemplate | null>(null);

  const done = (message: string) => ({
    onSuccess: () => {
      setEditing(null);
      toast({ tone: 'success', message });
    },
    onError: (e: unknown) => toast({ tone: 'error', message: errorMessage(e) }),
  });

  const start = (t?: RejectionTemplate) => {
    setEditing(t ? t.id : 'new');
    setLabel(t?.label ?? '');
    setBody(t?.body ?? '');
    setOrder(String(t?.sort_order ?? items.length));
  };

  const save = () => {
    const sort_order = Number.parseInt(order, 10);
    /* Only the open form reaches here, so `editing` is set: `'new'` or the row's id. */
    if (editing === 'new') create.mutate({ label, body, sort_order }, done('Reason added'));
    else update.mutate({ id: Number(editing), label, body, sort_order }, done('Reason saved'));
  };

  const form = (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)', marginTop: 'var(--sp-sm)' }}>
      <Input density="admin" label="Chip label (reviewers only)" value={label} onChange={setLabel} />
      <Input
        density="admin"
        multiline
        label="What the pandit reads"
        value={body}
        onChange={setBody}
        helper={`${body.length} / 500 · checked by the content lint on save`}
        error={body.length > 500 ? `${body.length - 500} characters over the 500 the document keeps` : undefined}
      />
      <Input
        density="admin"
        label="Position"
        inputMode="numeric"
        value={order}
        onChange={setOrder}
        error={positionError(order) ?? undefined}
      />
      <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
        <ScopeAction
          scope={scope}
          need="supply_kyc"
          size="sm"
          disabled={!label.trim() || !body.trim() || body.length > 500 || positionError(order) !== null}
          onClick={() => setConfirming('save')}
        >
          Save reason
        </ScopeAction>
        <ScopeAction scope={scope} need="supply_kyc" size="sm" variant="secondary" onClick={() => setEditing(null)}>
          Cancel
        </ScopeAction>
      </div>
    </div>
  );

  const toggling = confirming !== null && confirming !== 'save' ? confirming : null;
  const confirmDialog = (
    <ConfirmAction
      open={confirming !== null}
      title={toggling ? `${toggling.is_active ? 'Retire' : 'Restore'} “${toggling.label}”?` : 'Save this reason?'}
      phrase={toggling ? (toggling.is_active ? 'RETIRE' : 'RESTORE') : 'SAVE'}
      consequences={
        toggling
          ? [
              toggling.is_active
                ? 'Reviewers stop being offered it. Rejections already sent with it are untouched.'
                : 'Reviewers are offered it again, in its old position.',
            ]
          : [
              'Reviewers are offered this text in the reject dialog, and a pandit reads it word for word.',
              'Rejections already sent keep the words they went out with.',
            ]
      }
      confirmLabel={toggling ? (toggling.is_active ? 'Retire' : 'Restore') : 'Save reason'}
      onCancel={() => setConfirming(null)}
      onConfirm={() => {
        setConfirming(null);
        if (toggling) {
          update.mutate({ id: toggling.id, is_active: !toggling.is_active }, done(toggling.is_active ? 'Reason retired' : 'Reason restored'));
        } else {
          save();
        }
      }}
    />
  );

  return (
    <Section
      title="Rejection reasons"
      note="The quick-picks in the reject dialog. A pick is copied into the reason, so editing one never changes a rejection already sent."
      actions={
        <ScopeAction scope={scope} need="supply_kyc" size="sm" onClick={() => start()}>
          Add reason
        </ScopeAction>
      }
    >
      {list.isPending && <LoadingState message="Reading the reasons…" />}
      {list.isError && <ErrorState title="Could not read the rejection reasons" cause={errorMessage(list.error)} />}
      {editing === 'new' && form}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--sp-sm)' }}>
        {items.map((t) => (
          <li key={t.id} aria-label={t.label}>
            <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
              <strong>{t.label}</strong>
              {!t.is_active && <StatusChip tone="neutral" label="retired" />}
              <span style={{ flex: 1 }} />
              <ScopeAction scope={scope} need="supply_kyc" size="sm" variant="secondary" onClick={() => start(t)}>
                Edit
              </ScopeAction>
              <ScopeAction
                scope={scope}
                need="supply_kyc"
                size="sm"
                variant="secondary"
                onClick={() => setConfirming(t)}
              >
                {t.is_active ? 'Retire' : 'Restore'}
              </ScopeAction>
            </div>
            <div style={{ color: 'var(--c-ink2)' }}>{t.body}</div>
            {editing === t.id && form}
          </li>
        ))}
      </ul>
      {confirmDialog}
    </Section>
  );
}
